"use client";

// Рендерер турнирной сетки для РЕДАКТОРА и ПРЕВЬЮ (по макету Claude Design):
// колонки раундов + измеряемые SVG-связи, кликабельные заголовки раундов
// (BO на весь раунд), секции Winners/Losers/Гранд-финал для DE.
// Зрительский вид (вкладка «Сетка») остаётся на ReactFlow (BracketFlow) —
// этот компонент его не трогает.

import { useTranslations } from "next-intl";

import { Check, ChevronDown, Lock, Pause, Pencil, Play, Undo2 } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";

import type {
  BracketData,
  BracketMatch,
  BracketTeam,
} from "@/entities/bracket/model/types";
import {
  isLiveStatus,
  isPausedStatus,
  normalizeMatchStatus,
  scoreOutcome,
} from "@/entities/bracket/lib/match-status";
import { ScoreValue } from "@/entities/bracket/ui/score-value";
import { PoolBadge } from "@/features/match-champion-pools/ui/pool-badge";
import { ProfilePreviewTrigger } from "@/features/profile-preview/ui/profile-preview-trigger";
import { getAvatarUrl } from "@/shared/lib/image-utils";

const BO_OPTIONS = [1, 3, 5] as const;
type Section = "winners" | "losers" | "finals";

interface RoundGroup {
  roundIndex: number;
  bo: 1 | 3 | 5;
  title: string;
  matches: BracketMatch[];
}

/**
 * Подпись раунда. Функция осталась ЧИСТОЙ: словарь приходит параметром, а не хуком.
 *
 * Хук здесь стоять не может — она вызывается из `groupRounds`, обычной функции без
 * React-контекста. Но и оставлять русские строки нельзя было: на английской локали
 * сетка целиком говорила по-русски.
 *
 * Ключ с номером (`{n}`) вместо склейки префикса с числом: в разных языках порядок
 * слов разный, и «WB » + «Раунд 3» собирается только по-русски.
 */
type RoundLabeller = (key: string, values?: Record<string, number>) => string;

// ordinal — порядковый номер раунда в секции (1-based), total — число раундов.
function roundTitle(
  section: Section,
  ordinal: number,
  total: number,
  t: RoundLabeller,
): string {
  if (section === "finals") return t("grandFinal");
  if (section === "losers") {
    return ordinal >= total ? t("lbFinal") : t("lbRound", { n: ordinal });
  }

  // winners / single elimination
  if (section === "winners") {
    if (ordinal >= total) return t("wbFinal");
    if (ordinal === total - 1) return t("wbSemifinal");
    return t("wbRound", { n: ordinal });
  }

  if (ordinal >= total) return t("final");
  if (ordinal === total - 1) return t("semifinal");
  if (ordinal === total - 2) return t("quarterfinals");
  return t("round", { n: ordinal });
}

function groupRounds(
  matches: BracketMatch[],
  section: Section,
  t: RoundLabeller,
): RoundGroup[] {
  const inSection = matches.filter(
    (m) => (m.bracket ?? "winners") === section,
  );
  if (inSection.length === 0) return [];
  const byRound = new Map<number, BracketMatch[]>();
  for (const m of inSection) {
    const r = m.roundIndex ?? 0;
    if (!byRound.has(r)) byRound.set(r, []);
    byRound.get(r)!.push(m);
  }
  const sorted = [...byRound.entries()].sort((a, b) => a[0] - b[0]);
  const total = sorted.length;
  return sorted.map(([roundIndex, ms], i) => ({
    roundIndex,
    bo: (ms[0]?.bestOf ?? 1) as 1 | 3 | 5,
    title: roundTitle(section, i + 1, total, t),
    matches: ms,
  }));
}

function winnerSideOf(m: BracketMatch): "a" | "b" | null {
  const done = normalizeMatchStatus(m.status) === "completed";
  if (!done) return null;
  const sa = m.sides[0]?.score ?? 0;
  const sb = m.sides[1]?.score ?? 0;
  if (sa === sb) return null;
  return sa > sb ? "a" : "b";
}

const ACCENT = "var(--primary)";

// KAN-794: подсветка исхода в ячейке матча. Прозрачные, а не плотные: ячейка
// стоит на фоне карточки, и плотная заливка перебивала бы золотую рамку
// гранд-финала и рамку Live-матча.
// Едва заметные: подсветка должна подсказывать исход, а не заливать ячейку.
const WIN_BG = "color-mix(in srgb, var(--score-win) 7%, transparent)";
const LOSS_BG = "color-mix(in srgb, var(--score-loss) 6%, transparent)";
const CONN = "color-mix(in srgb, var(--foreground) 18%, transparent)";
// Обводка гранд-финала. Токен статуса «золото» в теме отсутствует, а --primary
// уже занят победителем и LIVE — берём отдельный акцент.
const GOLD = "color-mix(in srgb, #c9a227 78%, var(--border))";

function RoundHeader({
  title,
  bo,
  editable,
  onChange,
}: {
  title: string;
  bo: 1 | 3 | 5;
  editable?: boolean;
  onChange?: (bo: 1 | 3 | 5) => void;
}) {
  const t = useTranslations("Bracket.board");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);
  return (
    <div className="relative mb-3 flex h-7 items-center">
      <button
        type="button"
        onClick={(e) => {
          if (!editable) return;
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title={editable ? t("seriesFormatHint") : undefined}
        className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 ${
          editable ? "cursor-pointer hover:bg-accent" : "cursor-default"
        } ${open ? "bg-primary/10" : ""}`}
      >
        <span className="whitespace-nowrap text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <span
          className={`rounded-full border px-1.5 py-px text-[9px] font-bold tracking-wide ${
            editable
              ? "border-primary/30 bg-primary/15 text-primary"
              : "border-border bg-secondary text-muted-foreground"
          }`}
        >
          BO{bo}
        </span>
        {editable && <ChevronDown className="h-3 w-3 text-muted-foreground" />}
      </button>
      {open && (
        <div
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          className="absolute left-0 top-9 z-20 min-w-[168px] rounded-lg border border-border bg-popover p-1.5 shadow-lg"
        >
          <div className="px-2 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            {t("seriesFormat")}
          </div>
          {BO_OPTIONS.map((opt) => {
            const on = opt === bo;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange?.(opt);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm font-semibold ${
                  on ? "bg-primary/10 text-primary" : "hover:bg-accent"
                }`}
              >
                <span className="whitespace-nowrap">
                  Best of {opt}{" "}
                  <span className="font-medium text-muted-foreground">· BO{opt}</span>
                </span>
                {on && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MatchNode({
  match,
  teamsLookup,
  playerUserIdByTeamId,
  clickable,
  showBadge,
  tournamentId,
  poolInfo,
  onClick,
  onUnlive,
  onPause,
  onResume,
  nodeRef,
  big = false,
}: {
  match: BracketMatch;
  teamsLookup: Record<string, BracketTeam>;
  /** teamId -> userId игрока (1v1: команда из одного участника) для hover-превью профиля. */
  playerUserIdByTeamId?: Record<string, string>;
  /** Узел кликабелен (есть обработчик и матч укомплектован). */
  clickable?: boolean;
  /** Показывать угловой edit-бейдж (замок/карандаш) — только в редакторе. */
  showBadge?: boolean;
  onClick?: () => void;
  /** Вернуть матч из эфира (только организатору и только для Live). */
  onUnlive?: () => void;
  /** Поставить живой матч на паузу (только организатору, KAN-1647). */
  onPause?: () => void;
  /** Продолжить матч с паузы (только организатору, KAN-1647). */
  onResume?: () => void;
  nodeRef?: (el: HTMLDivElement | null) => void;
  /** Гранд-финал: шире, выше и с золотой рамкой — главный матч турнира. */
  big?: boolean;
  /** KAN-1676: турнир и сводка пулов для бейджа «Пулы 6+6». */
  tournamentId?: string;
  poolInfo?: { publishedGames: number; poolSize: number };
}) {
  const t = useTranslations("Bracket.board");
  const win = winnerSideOf(match);
  const isDone = win !== null;
  const isLive = isLiveStatus(match.status);
  const isPaused = isPausedStatus(match.status);
  // KAN-1647: при нулевом лимите пауз сервер всё равно отклонит запрос — кнопку
  // не предлагаем вовсе (правило QA: недоступное действие не показываем).
  const pausesAllowed = match.pausesAllowedPerMatch ?? 0;
  const isCancelled = normalizeMatchStatus(match.status) === "cancelled";

  const row = (side: "a" | "b") => {
    const idx = side === "a" ? 0 : 1;
    const teamId = match.sides[idx]?.teamId;
    const team = teamId ? teamsLookup[teamId] : undefined;
    const score = match.sides[idx]?.score;
    const outcome = scoreOutcome(
      score,
      match.sides[idx === 0 ? 1 : 0]?.score,
      match.status,
      match.isTechnicalWin,
    );
    // KAN-794: исход читается фоном строки, а не полоской сбоку. Полоска была
    // одного цвета у победителя и отсутствовала у проигравшего — то есть
    // проигравший не отличался от несыгранного матча. Зелёный и красный дают
    // исход с одного взгляда, оставаясь прозрачными, чтобы не спорить с
    // золотой рамкой гранд-финала и подсветкой Live.
    return (
      <div
        className={`flex ${big ? "h-[42px]" : "h-[30px]"} items-center gap-2 px-2.5`}
        style={
          isDone
            ? { background: outcome === "winner" ? WIN_BG : outcome === "loser" ? LOSS_BG : undefined }
            : undefined
        }
      >
        {/* KAN-795: логотип команды. Ячейка сетки — единственное место, где команду
            видно на всём турнире, и до сих пор она была там только текстом. Дефолт
            подставляем всегда: дыры вместо логотипов рвут выравнивание строк. */}
        <img
          src={getAvatarUrl(team?.avatar)}
          alt=""
          className={`shrink-0 rounded-sm object-cover ${big ? "h-5 w-5" : "h-4 w-4"}`}
        />
        {team?.seed != null ? (
          <span className="w-3 text-center font-mono text-[10px] text-muted-foreground">
            {team.seed}
          </span>
        ) : null}
        {(() => {
          const nameText = `${big ? "text-[15px]" : "text-[13px]"} ${
            outcome === "winner" ? "font-bold" : "font-medium"
          } ${team?.name ? "text-foreground" : "italic text-muted-foreground"}`;
          // KAN-1622: триггер превью профиля рендерит обычный div. Когда flex-ограничения
          // (min-w-0 flex-1 truncate) стояли только на вложенном span, сам wrapper не
          // сжимался ниже ширины длинного имени (min-width:auto у flex-элемента) и
          // выталкивал счёт за пределы карточки сетки. Поэтому ограничения перенесены
          // на wrapper-триггер, а span внутри остаётся block + truncate.
          const nameClassName = `min-w-0 flex-1 truncate ${nameText}`;
          const uid = teamId ? playerUserIdByTeamId?.[teamId] : undefined;
          return uid ? (
            <ProfilePreviewTrigger userId={uid} className="min-w-0 flex-1">
              <span className={`block truncate ${nameText} cursor-pointer hover:underline`}>
                {team?.name ?? "—"}
              </span>
            </ProfilePreviewTrigger>
          ) : (
            <span className={nameClassName}>{team?.name ?? "—"}</span>
          );
        })()}
        {score != null && (
          <ScoreValue
            outcome={outcome}
            score={score}
            className={`shrink-0 font-mono ${big ? "text-[15px]" : "text-[13px]"}`}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${big ? "w-[268px]" : "w-[208px]"}`} ref={nodeRef}>
      <div
        {...(clickable && onClick
          ? {
              // Карточка — кнопка только когда кликабельна; внутри есть свои элементы,
              // поэтому клавиатура ловится только на самой карточке.
              role: "button",
              tabIndex: 0,
              onClick,
              onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
                if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onClick();
                }
              },
            }
          : {})}
        className={`overflow-hidden rounded-lg ${big ? "border-2 shadow-md" : "border shadow-sm"} bg-card transition-shadow`}
        style={{
          borderColor: isLive
            ? `color-mix(in srgb, ${ACCENT} 55%, var(--border))`
            : big
              ? GOLD
              : "var(--border)",
          cursor: clickable ? "pointer" : "default",
        }}
      >
        {row("a")}
        <div className="h-px bg-border" />
        {row("b")}
        {poolInfo && tournamentId && (
          <PoolBadge
            tournamentId={tournamentId}
            matchId={match.id}
            poolSize={poolInfo.poolSize}
            publishedGames={poolInfo.publishedGames}
          />
        )}
      </div>
      {isCancelled && (
        <span className="mt-1 block text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("cancelledResult")}
        </span>
      )}
      {showBadge && (
        <span
          title={isDone ? t("matchDone") : t("matchEnterResult")}
          className="absolute -right-[7px] -top-[7px] flex h-[18px] w-[18px] items-center justify-center rounded-full border bg-card shadow-sm"
          style={{ color: isDone ? "var(--muted-foreground)" : ACCENT }}
        >
          {isDone ? <Lock className="h-2.5 w-2.5" /> : <Pencil className="h-2.5 w-2.5" />}
        </span>
      )}
      {(isLive || isPaused) && (
        <span className="absolute -bottom-2 left-2.5 flex items-center gap-1">
          {isLive && (
            <span className="rounded-full px-1.5 py-px text-[9px] font-bold text-white" style={{ background: "var(--status-live, #ef4444)" }}>
              LIVE
            </span>
          )}
          {/* KAN-1647: до фикса пауза была видна только в ReactFlow-рендерере
              (зрительский вид), а организаторский BracketBoard для SE/DE просто
              прятал LIVE-метку и не давал ни поставить паузу, ни продолжить. */}
          {isPaused && (
            <span className="rounded-full bg-amber-500 px-1.5 py-px text-[9px] font-bold text-white">
              {t("pausedBadge")}
            </span>
          )}
          {isLive && onPause && pausesAllowed > 0 && (
            <button
              type="button"
              title={t("pauseMatch")}
              aria-label={t("pauseMatch")}
              onClick={(e) => {
                e.stopPropagation();
                onPause();
              }}
              className="flex h-[15px] w-[15px] items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground"
            >
              <Pause className="h-2.5 w-2.5" />
            </button>
          )}
          {isPaused && onResume && (
            <button
              type="button"
              title={t("resumeMatch")}
              aria-label={t("resumeMatch")}
              onClick={(e) => {
                e.stopPropagation();
                onResume();
              }}
              className="flex h-[15px] w-[15px] items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground"
            >
              <Play className="h-2.5 w-2.5" />
            </button>
          )}
          {/* KAN-371: единственная точка, где организатор SE/DE-сетки может вернуть
              матч из эфира. Пункты «Матч начался»/«Пауза» живут в ReactFlow-рендерере
              (match-node), а он для SE/DE не используется — там показывается этот
              BracketBoard, у которого lifecycle-управления не было вовсе. */}
          {isLive && onUnlive && (
            <button
              type="button"
              title={t("backFromLive")}
              aria-label={t("backFromLive")}
              onClick={(e) => {
                e.stopPropagation();
                onUnlive();
              }}
              className="flex h-[15px] w-[15px] items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground"
            >
              <Undo2 className="h-2.5 w-2.5" />
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function Columns({
  rounds,
  teamsLookup,
  playerUserIdByTeamId,
  editableBo,
  manage,
  onSetBestOf,
  onMatchClick,
  onUnliveMatch,
  onPauseMatch,
  onResumeMatch,
  tournamentId,
  poolSummary,
  height = 360,
  big = false,
}: {
  rounds: RoundGroup[];
  teamsLookup: Record<string, BracketTeam>;
  playerUserIdByTeamId?: Record<string, string>;
  editableBo?: boolean;
  manage?: boolean;
  onSetBestOf?: (matchId: string, bo: 1 | 3 | 5) => void;
  onMatchClick?: (matchId: string) => void;
  onUnliveMatch?: (matchId: string) => void;
  /** Поставить живой матч на паузу (KAN-1647). */
  onPauseMatch?: (matchId: string) => void;
  /** Продолжить матч с паузы (KAN-1647). */
  onResumeMatch?: (matchId: string) => void;
  height?: number;
  /** Крупные карточки — секция гранд-финала. */
  big?: boolean;
  /** KAN-1676: турнир и сводка пулов для бейджа в карточке матча. */
  tournamentId?: string;
  poolSummary?: Map<string, { publishedGames: number; poolSize: number }>;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [paths, setPaths] = useState<string[]>([]);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    // KAN-299: все чтения геометрии — одним проходом с кэшем rect'ов и внутри
    // rAF (ResizeObserver/resize дёргали синхронный getBoundingClientRect на
    // каждый edge дважды → forced reflow 42–68мс)
    let rafId = 0;
    function compute() {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const base = wrap.getBoundingClientRect();
      const rectCache = new Map<string, DOMRect>();
      const rectOf = (id: string): DOMRect | null => {
        const cached = rectCache.get(id);
        if (cached) return cached;
        const el = nodeRefs.current[id];
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        rectCache.set(id, rect);
        return rect;
      };
      const out: string[] = [];
      for (const round of rounds) {
        for (const m of round.matches) {
          for (const nextId of m.nextMatchIds ?? []) {
            const f = rectOf(m.id);
            const t = rectOf(nextId);
            if (!f || !t) continue;
            const x1 = f.right - base.left;
            const y1 = f.top + f.height / 2 - base.top;
            const x2 = t.left - base.left;
            const y2 = t.top + t.height / 2 - base.top;
            const xm = (x1 + x2) / 2;
            out.push(`M ${x1} ${y1} H ${xm} V ${y2} H ${x2}`);
          }
        }
      }
      setPaths(out);
      setDims({ w: wrap.scrollWidth, h: wrap.scrollHeight });
    }
    function schedule() {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        compute();
      });
    }
    compute();
    const ro = new ResizeObserver(schedule);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", schedule);
    const t = setTimeout(schedule, 60);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", schedule);
      clearTimeout(t);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [rounds]);

  return (
    <div
      ref={wrapRef}
      // Горизонтальный скролл нужен только там, где раундов больше одного и они
      // реально могут не поместиться. У гранд-финала колонка одна, шире своего
      // содержимого он не бывает — а полоса прокрутки там всё равно иногда
      // вылезала и выглядела мусором. Единственная колонка не скроллится.
      className={`relative flex gap-14 px-2 pb-6 pt-3.5 ${
        rounds.length > 1 ? "overflow-x-auto" : "overflow-x-visible"
      }`}
      // max(Npx, 100%): секция не ниже своего минимума, но при этом заполняет
      // карточку, если ту растянули до высоты соседа по ряду. Без 100% матч
      // гранд-финала прилипал бы к верху, а низ карточки оставался пустым;
      // без Npx одиночные секции (Losers) схлопывались бы по содержимому.
      style={{ minHeight: height > 0 ? `max(${height}px, 100%)` : "100%" }}
    >
      <svg
        width={dims.w}
        height={dims.h}
        className="pointer-events-none absolute inset-0 z-0"
      >
        {paths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={CONN} strokeWidth="2" />
        ))}
      </svg>
      {rounds.map((round) => (
        <div
          key={round.roundIndex}
          className="relative z-[1] flex flex-none flex-col self-stretch"
        >
          <RoundHeader
            title={round.title}
            bo={round.bo}
            editable={editableBo}
            onChange={(bo) => {
              const first = round.matches[0];
              if (first) onSetBestOf?.(first.id, bo);
            }}
          />
          <div className="flex flex-1 flex-col justify-around gap-4">
            {round.matches.map((m) => {
              const ready =
                Boolean(m.sides[0]?.teamId) && Boolean(m.sides[1]?.teamId);
              return (
                <MatchNode
                  key={m.id}
                  big={big}
                  match={m}
                  teamsLookup={teamsLookup}
                  playerUserIdByTeamId={playerUserIdByTeamId}
                  tournamentId={tournamentId}
                  poolInfo={poolSummary?.get(m.id)}
                  clickable={Boolean(onMatchClick) && ready}
                  showBadge={Boolean(manage) && ready}
                  onClick={() => onMatchClick?.(m.id)}
                  onUnlive={
                    manage && onUnliveMatch ? () => onUnliveMatch(m.id) : undefined
                  }
                  onPause={
                    manage && onPauseMatch ? () => onPauseMatch(m.id) : undefined
                  }
                  onResume={
                    manage && onResumeMatch ? () => onResumeMatch(m.id) : undefined
                  }
                  nodeRef={(el) => {
                    nodeRefs.current[m.id] = el;
                  }}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionCard({
  label,
  labelColor,
  children,
  className = "",
}: {
  label: string;
  labelColor?: string;
  children: React.ReactNode;
  /** Флексовые классы слота в ряду (flex-basis + min-w-0). */
  className?: string;
}) {
  return (
    // flex flex-col + grow на содержимом: когда карточку растянули по высоте
    // соседа по ряду, лишнюю высоту забирает не подпись, а сама сетка — матч
    // встаёт по центру своего блока, а не висит под заголовком.
    <div
      className={`flex flex-col rounded-xl border border-border bg-card/50 px-3.5 pb-2 pt-1 ${className}`}
    >
      <div
        className="pt-2 text-xs font-bold"
        style={{ color: labelColor ?? "var(--foreground)" }}
      >
        {label}
      </div>
      {/* grow (а не flex-1): базис остаётся auto, поэтому нерастянутая карточка
          по-прежнему меряется по содержимому и ничего не схлопывается. */}
      <div className="grow">{children}</div>
    </div>
  );
}

export interface BracketBoardProps {
  data: BracketData;
  teamsLookup: Record<string, BracketTeam>;
  /** teamId -> userId игрока (1v1: команда из одного участника) для hover-превью профиля. */
  playerUserIdByTeamId?: Record<string, string>;
  /** Черновик: формат серии раунда меняется кликом по заголовку. */
  editableBo?: boolean;
  /** Сетка идёт/опубликована: клик по укомплектованному матчу = управление. */
  manage?: boolean;
  onSetBestOf?: (matchId: string, bo: 1 | 3 | 5) => void;
  onMatchClick?: (matchId: string) => void;
  /** KAN-371: вернуть матч из эфира. Без него кнопка не рисуется. */
  onUnliveMatch?: (matchId: string) => void;
  /** KAN-1647: поставить живой матч на паузу. Без него кнопка не рисуется. */
  onPauseMatch?: (matchId: string) => void;
  /** KAN-1647: продолжить матч с паузы. Без него кнопка не рисуется. */
  onResumeMatch?: (matchId: string) => void;
  /** KAN-1676: бейдж пулов RandomDraft в карточках матчей (один запрос на страницу). */
  tournamentId?: string;
  poolSummary?: Map<string, { publishedGames: number; poolSize: number }>;
}

export function BracketBoard({
  data,
  teamsLookup,
  playerUserIdByTeamId,
  editableBo,
  manage,
  tournamentId,
  poolSummary,
  onSetBestOf,
  onMatchClick,
  onUnliveMatch,
  onPauseMatch,
  onResumeMatch,
}: BracketBoardProps) {
  const tBoard = useTranslations("Bracket.board");
  const common = {
    teamsLookup,
    playerUserIdByTeamId,
    editableBo,
    manage,
    tournamentId,
    poolSummary,
    onSetBestOf,
    onMatchClick,
    onUnliveMatch,
    onPauseMatch,
    onResumeMatch,
  };

  if (data.format === "double_elimination") {
    const winners = groupRounds(data.matches, "winners", tBoard);
    const losers = groupRounds(data.matches, "losers", tBoard);
    const finals = groupRounds(data.matches, "finals", tBoard);
    return (
      // Гранд-финал идёт в одном ряду с ВЕРХНЕЙ сеткой (а если места на двоих не
      // хватает — сразу под ней), но не под нижней. Раньше он висел в правом нижнем
      // углу рядом с нижней сеткой: нижняя на 8 командах длиннее верхней (4 раунда
      // против 3), из-за чего её приходилось поджимать, и она уезжала в скролл,
      // а главный матч турнира читался как придаток к сетке проигравших.
      <div className="flex flex-col gap-2">
        {/* ВНИМАНИЕ: три вещи ниже держат этот ряд вместе, убирать по одной нельзя.
            1) items-stretch — обе карточки ряда одной высоты (этого просил
               владелец). Растяжение работает построчно: когда ряд переносится и
               каждая карточка занимает всю ширину, в строке остаётся один
               элемент и тянуть уже нечего — лишней пустоты не появляется.
            2) min-w-0 на слотах — БЕЗ него у flex-элемента min-width:auto, и
               карточка Winners отказывается сжиматься ниже своего содержимого:
               в узкой колонке организатора (~906–1070px, справа панель
               «Управление сеткой») она вылезала из слота и реально перекрывала
               гранд-финал на ~195px. Растяжение (1) трогает только высоту и это
               не чинит — нужны ОБА: min-w-0 против наезда, stretch ради равной
               высоты.
            3) flex-basis = натуральная ширина содержимого секции (752 + 28
               паддингов у Winners, 284 + 28 у гранд-финала) — ряд переносится
               сам, когда на двоих места не хватает (< ~1088px): широко —
               гранд-финал справа от верхней сетки, узко — под ней. Порог считает
               flex-wrap от ФАКТИЧЕСКОГО контейнера, а не от ширины экрана: здесь
               ширина зависит от боковой панели, а не от вьюпорта, поэтому
               медиа-запрос (lg:) дал бы неверный ответ у организатора. */}
        <div className="flex flex-wrap items-stretch gap-2">
          <SectionCard
            className="min-w-0 flex-[3_1_780px]"
            label={tBoard("winners")}
            labelColor="var(--status-success-fg, #16a34a)"
          >
            <Columns rounds={winners} {...common} height={320} />
          </SectionCard>
          {/* У слота гранд-финала намеренно НЕТ min-w-0, в отличие от соседа:
              остаётся min-width:auto, и слот не сжимается уже своей карточки.
              Иначе его поджимала верхняя сетка и внутри вылезала полоса
              прокрутки. Сжиматься в этом ряду должна именно верхняя сетка —
              у неё колонок много и скролл там уместен. */}
          {finals.length > 0 && (
            <SectionCard className="flex-[1_1_300px]" label={tBoard("grandFinal")}>
              {/* height={0} — своей минимальной высоты у гранд-финала нет: она
                  целиком приходит от растянутой карточки, а единственный матч
                  центрируется в этой высоте (justify-around внутри Columns). */}
              <Columns rounds={finals} {...common} height={0} big />
            </SectionCard>
          )}
        </div>
        <SectionCard label={tBoard("losers")} labelColor="var(--status-live-fg, #ef4444)">
          <Columns rounds={losers} {...common} height={220} />
        </SectionCard>
      </div>
    );
  }

  // Single elimination (+ round-robin/swiss fall back to a single column set)
  const rounds = groupRounds(data.matches, "winners", tBoard);
  return (
    <div className="rounded-xl border border-border bg-card p-2">
      <Columns rounds={rounds} {...common} height={420} />
    </div>
  );
}
